import { FC } from 'react';
import { VStack } from '@chakra-ui/react';

import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon, { IconName } from 'components/_core/SunshineIcon';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import Tooltip from 'components/_core/Tooltip';
import Skeleton from 'components/Skeleton';

import { locales } from './locales';

type TransferType =
  | 'external_instant'
  | 'external_scheduled'
  | 'external_standard'
  | 'internal'
  | 'international';

const transferConfig = {
  external_instant: {
    cardDescription: locales.instantTransferCardDescription,
    cardTitle: locales.instantTransferCardTitle,
    iconBackgroundColor: 'blue',
    iconName: 'flash' as IconName,
    tooltipDescription: locales.instantTransferTooltipDescription,
  },
  external_scheduled: {
    cardDescription: locales.scheduledTransferCardDescription,
    cardTitle: locales.scheduledTransferCardTitle,
    iconBackgroundColor: 'grey',
    iconName: 'transaction-out' as IconName,
    tooltipDescription: locales.scheduledTransferTooltipDescription,
  },
  external_standard: {
    cardDescription: locales.externalStandardTransferCardDescription,
    cardTitle: locales.externalStandardTransferCardTitle,
    iconBackgroundColor: 'grey',
    iconName: 'transaction-out' as IconName,
    tooltipDescription: locales.externalStandardTransferTooltipDescription,
  },
  internal: {
    cardDescription: locales.internalTransferCardDescription,
    cardTitle: locales.internalTransferCardTitle,
    iconBackgroundColor: 'grey',
    iconName: 'payment' as IconName,
    tooltipDescription: locales.internalTransferTooltipDescription,
  },
  international: {
    cardDescription: null,
    cardTitle: locales.internationalTransferTitle,
    iconBackgroundColor: 'green',
    iconName: 'earth' as IconName,
    tooltipDescription: null,
  },
} as const;

interface TransferTypeCardProps {
  isLoading?: boolean;
  type: TransferType;
  description?: string | string[];
}

const TransferTypeCard: FC<TransferTypeCardProps> = ({
  description: descriptionProp,
  isLoading = false,
  type,
}) => {
  const {
    cardDescription,
    cardTitle,
    iconBackgroundColor,
    iconName,
    tooltipDescription,
  } = transferConfig[type];

  const description = descriptionProp ?? cardDescription;

  if (isLoading) {
    return (
      <VStack alignItems="start" spacing="space-4">
        <Text size="sm">{locales.title}</Text>
        <SunshineCard alignItems="center">
          <SunshineCard.Slot name="aside">
            <Skeleton
              borderRadius="radius-12"
              endColor="grey.300"
              height="40px"
              startColor="grey.100"
              width="40px"
            />
          </SunshineCard.Slot>
          <SunshineCard.Text width="50%">
            <Skeleton
              borderRadius="radius-24"
              endColor="grey.300"
              height="8px"
              startColor="grey.100"
              width="240px"
            />
            <Skeleton
              borderRadius="radius-24"
              endColor="grey.300"
              height="8px"
              marginTop="space-16"
              startColor="grey.100"
              width="150px"
            />
          </SunshineCard.Text>
        </SunshineCard>
      </VStack>
    );
  }

  return (
    <VStack alignItems="start" spacing="space-4">
      <Text size="sm">{locales.title}</Text>
      <SunshineCard alignItems="center">
        <SunshineCard.Slot name="aside">
          <SquircleIcon
            borderRadius="radius-12"
            colorScheme={iconBackgroundColor}
            name={iconName}
          />
        </SunshineCard.Slot>
        <SunshineCard.Title>{cardTitle}</SunshineCard.Title>
        {description ? (
          <SunshineCard.Text>{description}</SunshineCard.Text>
        ) : null}

        {tooltipDescription ? (
          <SunshineCard.Slot name="action">
            <Tooltip description={tooltipDescription} placement="top">
              <SunshineIcon
                color="grey.600"
                name="information"
                size="icon-24"
              />
            </Tooltip>
          </SunshineCard.Slot>
        ) : null}
      </SunshineCard>
    </VStack>
  );
};

export default TransferTypeCard;
